import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { Button, Modal, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { fetchUserNotifications, removeUserNotification, markNotificationRead } from '../http/notificationAPI';
import { Bell } from 'react-bootstrap-icons';
import { Context } from '../index';

const UserNotifications = ({ mobile }) => {
    const { user } = useContext(Context);
    const [show, setShow] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pushMessage, setPushMessage] = useState(null);
    const prevCountRef = useRef(0);

    const loadNotifications = useCallback(async () => {
        // Не загружаем уведомления, если пользователь не авторизован
        if (!user.isAuth) {
            return;
        }
        
        setLoading(true);
        setError('');
        try {
            const data = await fetchUserNotifications();
            setNotifications(data);
        } catch (e) {
            setError('Ошибка при загрузке уведомлений');
        } finally {
            setLoading(false);
        }
    }, [user.isAuth]);

    const handleRemove = async (id) => {
        try {
            await removeUserNotification(id);
            setNotifications(notifications.filter(n => n.id !== id));
        } catch (e) {
            setError('Ошибка при удалении уведомления');
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await markNotificationRead(id);
            setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch (e) {
            setError('Ошибка при обновлении уведомления');
        }
    };

    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    useEffect(() => {
        if (typeof notifications.length === 'number' && notifications.length > prevCountRef.current) {
            // Новое уведомление!
            const latest = notifications && notifications.length > 0 ? notifications[0].message : 'Новое уведомление!';
            setPushMessage(latest);
        }
        prevCountRef.current = notifications.length;
    }, [notifications]);

    const newNotifications = notifications.filter(n => !n.is_read);
    const oldNotifications = notifications.filter(n => n.is_read);
    const newCount = newNotifications.length;

    function forceOverlayCleanup() {
        const anyModalOpen = !!document.querySelector('.modal.show, .modal[style*="display: block"]');
        if (!anyModalOpen) {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            document.body.style.pointerEvents = '';
            document.documentElement.style.pointerEvents = '';
            // Полностью удаляем все overlay/backdrop из DOM
            document.querySelectorAll('.modal-backdrop, [data-rr-ui-backdrop], .modal-bg, .modal-overlay').forEach(el => {
                if (el.parentNode) el.parentNode.removeChild(el);
            });
        }
    }

    function hardBodyUnlock() {
        document.body.className = '';
        document.body.removeAttribute('style');
        document.body.removeAttribute('data-rr-ui-modal-open');
        document.body.style.overflow = '';
        document.body.style.pointerEvents = '';
        document.body.style.paddingRight = '';
        document.documentElement.style.pointerEvents = '';
    }

    const handleClose = () => {
        setShow(false);
        setTimeout(forceOverlayCleanup, 350);
        setTimeout(hardBodyUnlock, 400);
    };

    useEffect(() => {
        return () => {
            forceOverlayCleanup();
        };
    }, []);

    return (
        <>
            {mobile ? (
                <button
                    className="mobile-action-btn"
                    style={{ position: 'relative', zIndex: 1000 }}
                    onClick={() => setShow(true)}
                    tabIndex={0}
                    aria-label="Уведомления"
                >
                    <span style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <Bell size={22} />
                        {newCount > 0 && (
                            <span className="notification-badge-mobile">
                                {newCount}
                            </span>
                        )}
                        <span className="mobile-action-label mobile-action-label-center">Уведомл.</span>
                    </span>
                </button>
            ) : (
            <Button 
                variant="outline-secondary" 
                onClick={() => setShow(true)} 
                className="notification-button"
                style={{ 
                    width: '40px', 
                    height: '40px', 
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                }}
            >
                <Bell size={20} />
                {newCount > 0 && (
                    <Badge 
                        bg="danger" 
                        style={{ 
                            position: 'absolute', 
                            top: '-5px', 
                            right: '-5px',
                            padding: '4px 6px',
                            borderRadius: '50%',
                            fontSize: '12px'
                        }}
                    >
                        {newCount}
                    </Badge>
                )}
            </Button>
            )}
            
            {/* Модалка уведомлений только если show === true */}
            {show && (
                <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Уведомления</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!user.isAuth && (
                        <div className="text-center text-muted">
                            Для просмотра уведомлений необходимо войти в систему
                        </div>
                    )}
                    {user.isAuth && loading && <Spinner animation="border" />}
                    {user.isAuth && error && <div className="text-danger mb-2">{error}</div>}
                    {user.isAuth && (
                    <ListGroup>
                            {/* Новые уведомления */}
                            {newNotifications.length > 0 && <div style={{fontWeight:700, color:'#1976d2', margin:'8px 0 4px'}}>Новые</div>}
                            {newNotifications.map(n => (
                                <ListGroup.Item key={n.id} className="d-flex justify-content-between align-items-start" style={{background:'#e3f2fd'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        {n.image && <img src={n.image} alt="notif" style={{width:32,height:32,objectFit:'cover',borderRadius:6,marginRight:8}} />}
                                        <div>
                                            <b>{n.message}</b><br/>
                                            Заказ: {(n.order?.order_short_id || (n.orderId ? n.orderId.slice(0, 8) : n.orderId))} | {n.order ? new Date(n.order.created_at).toLocaleString() : ''}
                                        </div>
                                    </div>
                                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                        <Button variant="outline-success" size="sm" onClick={() => handleMarkRead(n.id)} title="Отметить как прочитанное">✓</Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleRemove(n.id)} title="Удалить уведомление">×</Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                            {/* Старые уведомления */}
                            {oldNotifications.length > 0 && <div style={{fontWeight:700, color:'#888', margin:'12px 0 4px'}}>Ранее</div>}
                            {oldNotifications.map(n => (
                                <ListGroup.Item key={n.id} className="d-flex justify-content-between align-items-start" style={{background:'#f5f5f5',opacity:0.7}}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                        {n.image && <img src={n.image} alt="notif" style={{width:32,height:32,objectFit:'cover',borderRadius:6,marginRight:8,filter:'grayscale(1)'}} />}
                                <div>
                                    <b>{n.message}</b><br/>
                                    Заказ: {(n.order?.order_short_id || (n.orderId ? n.orderId.slice(0, 8) : n.orderId))} | {n.order ? new Date(n.order.created_at).toLocaleString() : ''}
                                </div>
                                    </div>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleRemove(n.id)} title="Удалить уведомление">×</Button>
                            </ListGroup.Item>
                        ))}
                        {notifications.length === 0 && !loading && <div>Нет уведомлений</div>}
                    </ListGroup>
                    )}
                </Modal.Body>
            </Modal>
            )}
            <PushNotification message={pushMessage} onClose={() => setPushMessage(null)} />
        </>
    );
};

function PushNotification({ message, onClose }) {
    if (!message) return null;
    return (
        <div className="push-toast" style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2000,
            minWidth: 260,
            maxWidth: '90vw',
            display: 'flex',
            alignItems: 'center',
            gap: 16
        }}>
            <span style={{marginRight: 12, fontSize: 22, color: '#a21caf'}}>🔔</span>
            <span style={{flex: 1}}>{message}</span>
            <button onClick={onClose} style={{background: 'none', border: 'none', color: '#a21caf', fontWeight: 700, fontSize: 18, cursor: 'pointer', marginLeft: 8}}>✕</button>
        </div>
    );
}

export default UserNotifications; 